import {useDispatch, useSelector} from "react-redux";
import {useNavigate} from "react-router-dom";
import {useTranslation} from "react-i18next";
import React, {useEffect, useRef, useState} from "react";
import * as XLSX from "xlsx";
import jspreadsheet from "jspreadsheet-ce";
import {errorCodes, errorCodesNameValue} from "../../../enums/ErrorCodeExcel";
import {toast} from "react-toastify";
import {MODELS} from "../../../Apis/Models";

/**
 * @Author: Karen Yunuen Pozos Arias
 * @JobTitle: Full Stack Developer - IBL
 * @Email: kpozos@ibl.mx
 */

export const CommonCreationErrorsApp = ({moduleId, goBack, columns, validateSpaces = null}) => {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const { t } = useTranslation();
    const [contractorId, setContractorId] = useState(sessionStorage.getItem('contractorId'));
    const [supplierId, setSupplierId] = useState(sessionStorage.getItem('providerId'));

    const DEV = useSelector((state) => state?.CommonsSlice?.development); // Accessing development status from Redux state
    const { permission } = useSelector((state) => state.authenticatioauthennSlice); // Accessing user permissions from Redux state
    const { uploadFormFile } = useSelector((state) => state.FilesSlice);
    const { analizeCards } = useSelector((state) => state.CardsSlice);
    const excel = useSelector((state) => state.FilesSlice.excelFile);
    const [spreadsheetData, setSpreadsheetData] = useState(null);
    const [update, setUpdate] = useState(false);
    const [selectedErrorIndex, setSelectedErrorIndex] = useState(null);
    const [errorsArray, setErrorsArray] = useState([]);
    const [data, setData] = useState();
    const jRef = useRef(null);
    const [zoneData, setZoneData] = useState(null);
    const [roleData, setRoleData] = useState(null);
    const [departmentData, setDepartmentData] = useState(null);
    const jRefZone = useRef(null);
    const jRefRole = useRef(null);
    const jRefDepartment = useRef(null);
    const jRefNoError = useRef(null);

    const totalErrors = Array.isArray(errorsArray)
        ? errorsArray.reduce((acc, curr) => acc + (curr?.errors?.length || 0), 0)
        : 0;


    useEffect(() => {
        if (excel) {
            readExcelFile(excel);
        }
    }, [excel]);

    useEffect(() => {
        if (analizeCards) {
            setErrorsArray(analizeCards.data || []);
        } else if (uploadFormFile) {
            setErrorsArray(uploadFormFile.data || []);
        }else{
            navigate(goBack);
        }
    }, [analizeCards, uploadFormFile]);

    const readExcelFile = (file) => {
        if (!(file instanceof Blob)) {
            return;
        }
        const reader = new FileReader();
        reader.onload = (event) => {
            const binaryStr = event.target.result;
            const workbook = XLSX.read(binaryStr, { type: 'binary' });
            const firstSheetName = workbook.SheetNames[0];
            const worksheet = XLSX.utils.sheet_to_json(workbook.Sheets[firstSheetName], { header: 1, defval: '' });

            const filteredData = worksheet.map((row) => row.slice(0, columns.length));
            const cleanedData = filteredData.map((row, index) => {
                if (index >= 7 && row[5] && typeof row[5] === 'string') {
                    row[5] = row[5].replace(/\s/g, '');
                } else if (row[5] && typeof row[5] !== 'string') {
                    row[5] = String(row[5]).replace(/\s/g, '');
                }
                return row;
            });

            setSpreadsheetData(cleanedData.length > 0 ? cleanedData : null);
            if(moduleId === MODELS.Employee){
                const zoneData = worksheet.slice(8).map((row) => row.slice(15, 17));
                const roleData = worksheet.slice(8).map((row) => row.slice(18, 20));
                const departmentData = worksheet.slice(8).map((row) => row.slice(21, 23));
                setZoneData(zoneData.length > 0 ? zoneData : null);
                setRoleData(roleData.length > 0 ? roleData : null);
                setDepartmentData(departmentData.length > 0 ? departmentData : null);
            }
        };
        reader.readAsBinaryString(file);
    };

    const getFormData = () => {
        if(errorsArray.length === 1 && errorsArray[0]?.errors[0]?.errorCode === errorCodesNameValue.NO_DATA_FOUND_IN_THE_FILE){
            toast.warning(t(errorCodes[errorCodesNameValue.NO_DATA_FOUND_IN_THE_FILE]));
            return false;
        }

        const preservedRows = spreadsheetData.slice(0, 7);
        const cleanedRows = spreadsheetData
            .slice(7)
            .filter(row =>
                row.some(cell => cell !== '' && cell !== null && cell !== undefined)
            );

        const cleanedData = [...preservedRows, ...cleanedRows];

        const ws = XLSX.utils.aoa_to_sheet(cleanedData);
        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, 'Sheet1');
        const excelBlob = XLSX.write(wb, { bookType: 'xlsx', type: 'binary' });
        const arrayBuffer = new ArrayBuffer(excelBlob.length);
        const view = new Uint8Array(arrayBuffer);
        for (let i = 0; i < excelBlob.length; i++) {
            view[i] = excelBlob.charCodeAt(i) & 0xff;
        }
        String.fromCharCode.apply(null, view);
        let formData = new FormData();
        formData.append("id", contractorId || supplierId);
        formData.append(
            'file',
            new Blob([arrayBuffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' }),
            'errors.xlsx',
        );

        return formData;
    }


    useEffect(() => {
        if (spreadsheetData && jRef.current && errorsArray) {
            if (jRef.current.jexcel) {
                jRef.current.jexcel.destroy();
            }

            const jSpreadsheetInstance = jspreadsheet(jRef.current, {
                data: spreadsheetData,
                minDimensions: [2, 2],
                tableOverflow: true,
                columns: columns,
                validate: validate,
                onselection: (el, startCell, endCell) => {
                    const startCellName = `${String.fromCharCode(65 + startCell[0])}${startCell[1] + 1}`;
                    const cellErrorIndex = errorsArray.findIndex((errorItem, rowIndex) =>
                        errorItem?.errors?.some(
                            (error) =>
                                `${String.fromCharCode(65 + error.position - 1)}${rowIndex + 3}` === startCellName,
                        ),
                    );
                    if (cellErrorIndex !== -1) {
                        setSelectedErrorIndex(cellErrorIndex);
                    }
                },
            });

            const errorColor = getComputedStyle(document.documentElement).getPropertyValue('--error-code-600').trim();
            errorsArray.forEach((errorItem, dataIndex) => {
                if (errorItem.errors && Array.isArray(errorItem.errors)) {
                    errorItem?.errors?.forEach((error) => {
                        const cellName = `${String.fromCharCode(65 + error.position - 1)}${dataIndex + 9}`;
                        const errorMessage = t(errorCodes[error.errorCode]);
                        if (jSpreadsheetInstance.getCell(cellName)) {
                            jSpreadsheetInstance.setStyle(cellName, 'background-color', errorColor);
                            jSpreadsheetInstance.setComments(cellName, `Error: ${errorMessage}`);
                        }
                    });
                }
            });

            jRef.current.jexcel = jSpreadsheetInstance;
        }
    }, [spreadsheetData, errorsArray]);

    const isValidFile = (data) => {
       const errorsArray = data?.filter((item) => item.errors !== null);
        if (errorsArray.length > 0) {
            toast.warning(t('please_review_and_correct_the_identified_errors').toUpperCase());
        }else{
            setErrorsArray([]);
            setData(data);
        }
    }

    const validate = (cell, value) => {
        if(validateSpaces === null) return true;//No validate spaces

        if (cell[0] === validateSpaces && /\s/.test(value)) {
            return false;
        }
        return true;
    };

    // Returning all necessary handlers and state for use in the component
    return {
        t,
        DEV,
        permission,
        dispatch,
        navigate,
        uploadFormFile,
        analizeCards,
        excel,
        spreadsheetData,
        setSpreadsheetData,
        update,
        setUpdate,
        selectedErrorIndex,
        setSelectedErrorIndex,
        errorsArray,
        setErrorsArray,
        data,
        setData,
        jRef,
        totalErrors,
        getFormData,
        isValidFile,
        jspreadsheet,
        zoneData,
        jRefZone,
        roleData,
        jRefRole,
        departmentData,
        jRefDepartment,
        jRefNoError
    };
};

// Exporting the component for use in other parts of the application
export default CommonCreationErrorsApp;
